import { useMemo, useState } from 'react';
import { Box, DatePicker, FormField, Icon, IconButton, Select, Text } from '@zillow/constellation-10';
import { IconDownloadFilled } from '@zillow/constellation-icons';
import { useQuery } from '@zg-rentals/react-query5';
import { useMainLayout } from '../../../../layouts/MainPageLayout/MainLayoutContext';
import { GraphWrapper } from '../../../../SharedComponents/GraphWrapper';
import { FunnelBreakdownTable } from './FunnelBreakdownTable';
import { PropertyDetailModal } from '../../../AdSpend/Report/PropertyDetailModal';
import { TableType } from '../../../AdSpend/Report/AdSpendReport.config';
import {
  CustomerAnalyticsEnterpriseAnalyticsApiHandler_getListingPerformanceV2,
  CustomerAnalyticsEnterpriseAnalyticsApiHandler_getListingPerformanceV2_Path,
} from '@zg-rentals/ts-customer-analytics-client';
import { formatDateYyyyMmDd } from '../../../../../utils/dateUtils';
import { format } from 'date-fns/format';
import ExcelJS from 'exceljs';
import { formatNumber } from '../../../../../utils/formatNumber';
import { adproductsUtils } from '@zg-rentals/rental-revenue-components';

type TimeRangeOption = 'Yesterday' | 'Last 7 Days' | 'Last 30 Days' | 'Last 90 Days' | 'Custom';

const CUSTOM_TIME_RANGE = 'Custom';
const TIME_RANGE_OPTIONS: Array<TimeRangeOption> = [
  'Yesterday',
  'Last 7 Days',
  'Last 30 Days',
  'Last 90 Days',
  CUSTOM_TIME_RANGE,
];

const getDefaultStartDay = () => {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  date.setHours(0, 0, 0, 0);
  return date;
};

const getDefaultEndDay = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
};

const getMinStartDay = () => {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 1);
  date.setHours(0, 0, 0, 0);
  return date;
};

const getDateRangeFromOption = (
  option: TimeRangeOption,
  customStartDay?: Date,
  customEndDay?: Date,
): { startDay: Date; endDay: Date } => {
  const endDay = new Date();
  endDay.setHours(0, 0, 0, 0);
  const startDay = new Date(endDay);

  switch (option) {
    case 'Yesterday':
      startDay.setDate(startDay.getDate() - 1);
      return { startDay, endDay: new Date(startDay) };
    case 'Last 7 Days':
      startDay.setDate(startDay.getDate() - 7);
      break;
    case 'Last 30 Days':
      startDay.setDate(startDay.getDate() - 30);
      break;
    case 'Last 90 Days':
      startDay.setDate(startDay.getDate() - 90);
      break;
    case 'Custom':
      if (customStartDay && customEndDay) {
        return { startDay: customStartDay, endDay: customEndDay };
      }
      return { startDay: getDefaultStartDay(), endDay: getDefaultEndDay() };
  }

  return { startDay, endDay };
};

export const FunnelBreakdown = () => {
  const { filteredProperties, isFetchingInitialData } = useMainLayout();
  const [timeRange, setTimeRange] = useState<TimeRangeOption>('Last 30 Days');
  const [selectedProperty, setSelectedProperty] = useState<{
    listingAlias: string;
    propertyName: string;
  } | null>(null);
  const [customStartDay, setCustomStartDay] = useState<Date>(getDefaultStartDay());
  const [customEndDay, setCustomEndDay] = useState<Date>(getDefaultEndDay());

  const { startDay, endDay } = useMemo(
    () => getDateRangeFromOption(timeRange, customStartDay, customEndDay),
    [timeRange, customStartDay, customEndDay],
  );

  const { data: listingPerformanceData, isLoading } = useQuery({
    queryKey: [
      CustomerAnalyticsEnterpriseAnalyticsApiHandler_getListingPerformanceV2_Path,
      formatDateYyyyMmDd(startDay),
      formatDateYyyyMmDd(endDay),
    ],
    queryFn: () =>
      CustomerAnalyticsEnterpriseAnalyticsApiHandler_getListingPerformanceV2({
        startDayYyyyMmDd: formatDateYyyyMmDd(startDay),
        endDayYyyyMmDd: formatDateYyyyMmDd(endDay),
        includeFreeStats: true,
        includeMarketInsights: false,
      }),
    enabled: !isFetchingInitialData,
  });

  const filteredData = useMemo(() => {
    if (!listingPerformanceData || filteredProperties.length === 0) return listingPerformanceData;

    const propertyAliases = new Set(
      filteredProperties.map((prop) => prop.propertyDetails.listingAlias).filter(Boolean),
    );

    return {
      ...listingPerformanceData,
      analytics: listingPerformanceData.analytics.filter((listing) => propertyAliases.has(listing.listingAlias)),
    };
  }, [listingPerformanceData, filteredProperties]);

  const handleDownloadCSV = async () => {
    if (!filteredData || filteredData.analytics.length === 0) return;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Listing Performance');

    // Check if there are any single family listings
    const hasSingleFamily = filteredData.analytics.some((listing) => listing.isSingleFamily);

    // Check if there are any listings with spotlight days
    const hasSpotlightDays = filteredData.analytics.some((listing) => (listing.totalDaysSpotlighted ?? 0) > 0);

    // Define headers
    const headers = [
      'Property',
      'City',
      'State',
      'Advertising Type',
      'Published Status',
      'Impressions',
      'Listing Views',
      'Listing Web Referrals',
      'Total Leads',
      'Phone Leads',
      'Email Leads',
      'Ad Impressions',
      'Ad Clicks',
    ];

    if (hasSingleFamily) {
      headers.push('Days on Market');
    }

    if (hasSpotlightDays) {
      headers.push('Spotlight Days');
    }

    worksheet.addRow(headers);

    // Add data rows
    filteredData.analytics.forEach((listing) => {
      const unitName = listing.isSingleFamily ? (listing.unit || listing.unitName || '').toUpperCase() : '';
      const propertyName = listing.isSingleFamily
        ? unitName
          ? `${listing.street} ${unitName}`
          : listing.street
        : listing.listingName || listing.street;
      const advertisingType = adproductsUtils
        .mapProductTypes(listing.adproductTypes || [], listing.analyticsListingType)
        .join(', ')
        .replace('™', '');
      const row = [
        propertyName,
        listing.city || '',
        listing.state || '',
        advertisingType || '-',
        listing.publishedStatus || '-',
        formatNumber(listing.impressionCount),
        formatNumber(listing.viewCount),
        formatNumber(listing.listingWebReferrals),
        formatNumber(listing.leadCount),
        formatNumber(listing.phoneLeadCount),
        formatNumber(listing.emailLeadCount),
        formatNumber(listing.boostImpressions),
        formatNumber(listing.boostClickCount),
      ];

      if (hasSingleFamily) {
        row.push(formatNumber(listing.daysOnMarketCount));
      }

      if (hasSpotlightDays) {
        row.push(formatNumber(listing.totalDaysSpotlighted));
      }

      worksheet.addRow(row);
    });

    // Generate filename with date range
    const startDateStr = format(startDay, 'yyyy_MM_dd');
    const endDateStr = format(endDay, 'yyyy_MM_dd');
    const fileName = `Listing_Performance_${startDateStr}_to_${endDateStr}.xlsx`;

    // Download the file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const dateRangeText = `${format(startDay, 'MMM d, yyyy')} to ${format(endDay, 'MMM d, yyyy')}`;

  const timeRangeFilter = (
    <Box
      css={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: '40',
        alignItems: 'flex-end',
        '@media (max-width: 800px)': {
          gridTemplateColumns: '1fr',
        },
      }}
    >
      <Box>
        <FormField label="Time frame">
          <Select
            value={timeRange}
            onChange={(event) => setTimeRange(event.target.value as TimeRangeOption)}
            css={{ minWidth: '150px' }}
          >
            {TIME_RANGE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Select>
        </FormField>
      </Box>
      {timeRange === CUSTOM_TIME_RANGE && (
        <>
          <Box>
            <FormField label="Start">
              <DatePicker
                value={format(customStartDay, 'MM/dd/yyyy')}
                onValueChange={(
                  value: Date | string,
                  meta: {
                    reason: string;
                  },
                ) => {
                  if (meta.reason !== 'dropdown click') {
                    return;
                  }
                  const date = typeof value === 'string' ? new Date(value) : value;
                  setCustomStartDay(date);
                }}
                maxDate={customEndDay}
                minDate={getMinStartDay()}
                closeModalOnDateSelect
                dateFormat="MM/dd/yyyy"
              />
            </FormField>
          </Box>
          <Box>
            <FormField label="End">
              <DatePicker
                value={format(customEndDay, 'MM/dd/yyyy')}
                onValueChange={(
                  value: Date | string,
                  meta: {
                    reason: string;
                  },
                ) => {
                  if (meta.reason !== 'dropdown click') {
                    return;
                  }
                  const date = typeof value === 'string' ? new Date(value) : value;
                  setCustomEndDay(date);
                }}
                minDate={customStartDay}
                maxDate={getDefaultEndDay()}
                closeModalOnDateSelect
                dateFormat="MM/dd/yyyy"
              />
            </FormField>
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <>
      <Box css={{ position: 'relative' }}>
        <Box css={{ position: 'absolute', top: '600', right: '600', zIndex: 10 }}>
          <IconButton
            emphasis="outlined"
            shape="square"
            size="sm"
            title="download"
            tone="neutral"
            onClick={handleDownloadCSV}
            disabled={!filteredData || filteredData.analytics.length === 0}
          >
            <Icon>
              <IconDownloadFilled />
            </Icon>
          </IconButton>
        </Box>
        <GraphWrapper
          graphId="funnel-breakdown"
          title="Listing performance"
          subtitle={`A detailed breakdown of your listings across key metrics from ${dateRangeText}`}
          customFilters={timeRangeFilter}
          infoCards={[]}
          loading={isLoading || isFetchingInitialData}
        >
          <Box css={{ marginBottom: '400' }}>
            <Text textStyle="body-sm">{dateRangeText}</Text>
          </Box>
          <FunnelBreakdownTable
            data={filteredData}
            onRowClick={(listingAlias, propertyName) => setSelectedProperty({ listingAlias, propertyName })}
            isLoading={isLoading || isFetchingInitialData}
          />
        </GraphWrapper>
      </Box>

      {selectedProperty && (
        <PropertyDetailModal
          isOpen={true}
          onClose={() => setSelectedProperty(null)}
          listingAlias={selectedProperty.listingAlias}
          propertyName={selectedProperty.propertyName}
          startDate={formatDateYyyyMmDd(startDay)}
          endDate={formatDateYyyyMmDd(endDay)}
          tableType={TableType.ALL}
        />
      )}
    </>
  );
};
